import { useEffect, useRef, useState } from 'react';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import { useThreadMessages } from './useThreadMessages';
import { useThreadSubscription } from './useThreadSubscription';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { Avatar, IconButton, Icon } from '../ui';

interface ThreadPanelProps {
  threadId: string;
  onClose: () => void;
}

function UserAvatar({ name, avatar, isOwlbert = false }: { name: string; avatar: string | null; isOwlbert?: boolean }) {
  // Owlbert keeps the owl emoji as its avatar (user content)
  if (isOwlbert && !avatar) {
    return (
      <span
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: 24,
          height: 24,
          background: 'var(--status-warn-soft)',
          border: '1px solid var(--border-hairline)',
        }}
      >
        <span style={{ fontSize: '11px' }}>🦉</span>
      </span>
    );
  }

  return <Avatar name={name} src={avatar} size={24} />;
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="hover-underline"
            style={{ color: 'var(--accent-text)' }}
          >
            {children}
          </a>
        ),
        code: ({ children, className }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <code
                className="px-1.5 py-0.5"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-xs)',
                  color: 'var(--text-body)',
                }}
              >
                {children}
              </code>
            );
          }
          return (
            <code
              className="block p-3 my-1.5 overflow-x-auto"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-body)',
              }}
            >
              {children}
            </code>
          );
        },
        strong: ({ children }) => (
          <strong style={{ fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => (
          <ul className="list-disc list-inside ml-1 mb-1">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside ml-1 mb-1">{children}</ol>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function RootMessageCard({ message }: { message: any }) {
  const isOwlbert = message.user.name.toLowerCase().includes('owlbert');
  const isDeleted = !!message.deleted_at;

  return (
    <div
      className="p-3"
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderLeft: '3px solid var(--accent)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <div className="flex items-start gap-2.5">
        <UserAvatar name={message.user.name} avatar={message.user.avatar} isOwlbert={isOwlbert} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1">
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: isOwlbert ? 'var(--accent-text)' : 'var(--text-body)',
              }}
            >
              {message.user.name}
              {isOwlbert && ' 🦉'}
            </span>
            <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
            </span>
          </div>

          {isDeleted ? (
            <div className="italic" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              [This message was deleted]
            </div>
          ) : (
            <>
              {message.content && (
                <div
                  style={{
                    fontSize: 'var(--text-base)',
                    lineHeight: 'var(--leading-normal)',
                    color: 'var(--text-body)',
                  }}
                >
                  <MarkdownContent content={message.content} />
                </div>
              )}
              {message.edited_at && (
                <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
                  (edited)
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ThreadPanel({ threadId, onClose }: ThreadPanelProps) {
  const { messages, rootMessage, loading, error, sendReply, editMessage, deleteMessage } = useThreadMessages(threadId);
  const { isMuted, toggleMute } = useThreadSubscription(threadId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Handle scroll position for showing scroll-to-bottom button
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isAtBottom && messages.length > 5);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const replyCount = rootMessage?.reply_count || 0;

  return (
    <div
      className="flex flex-col h-full"
      style={{
        background: 'var(--surface-card)',
        borderLeft: '1px solid var(--border-hairline)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-hairline)' }}
      >
        <span className="t-eyebrow">Thread</span>

        <div className="flex items-center gap-1">
          {/* Mute/Unmute button */}
          <IconButton
            icon={isMuted ? 'bell-off' : 'bell'}
            size="sm"
            label={isMuted ? 'Unmute thread' : 'Mute thread'}
            onClick={toggleMute}
            style={isMuted ? { color: 'var(--danger-400)' } : undefined}
          />

          {/* Close button */}
          <IconButton icon="x" size="sm" label="Close thread" onClick={onClose} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {error && (
          <div
            className="mx-4 mt-4 p-3"
            style={{
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {loading && !rootMessage && (
          <div className="flex-1 flex items-center justify-center">
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              Loading thread...
            </div>
          </div>
        )}

        {rootMessage && (
          <>
            {/* Scrollable area: root message + replies */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto min-h-0 chat-scrollbar"
            >
              {/* Root message */}
              <div className="p-3.5">
                <RootMessageCard message={rootMessage} />
              </div>

              {/* Replies separator */}
              <div
                className={`flex items-center gap-3 mx-3.5 mb-4 ${
                  replyCount === 0 ? 'hidden' : ''
                }`}
              >
                <span className="flex-1 h-px" style={{ background: 'var(--border-hairline)' }} />
                <span className="t-eyebrow">
                  {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </span>
                <span className="flex-1 h-px" style={{ background: 'var(--border-hairline)' }} />
              </div>

              {/* Thread replies */}
              {messages.length === 0 && !loading && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center" style={{ color: 'var(--text-faint)' }}>
                    <Icon
                      name="messages-square"
                      size={28}
                      className="mx-auto mb-2"
                      style={{ opacity: 0.5 }}
                    />
                    <p style={{ fontSize: 'var(--text-sm)' }}>No replies yet</p>
                  </div>
                </div>
              )}

              {messages.map((message, index) => {
                const prevMessage = index > 0 ? messages[index - 1] : null;
                const isGrouped = prevMessage?.user_id === message.user_id &&
                  new Date(message.created_at).getTime() - new Date(prevMessage.created_at).getTime() < 10 * 60 * 1000;

                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isGrouped={isGrouped}
                    showTimeSeparator={false}
                    onEdit={editMessage}
                    onDelete={deleteMessage}
                  />
                );
              })}

              <div ref={messagesEndRef} />
            </div>

            {/* Scroll to bottom button */}
            {showScrollToBottom && (
              <div className="absolute bottom-20 right-4">
                <IconButton
                  icon="chevron-down"
                  variant="outline"
                  size="sm"
                  label="Scroll to bottom"
                  onClick={scrollToBottom}
                  style={{ borderRadius: 'var(--radius-pill)', boxShadow: 'var(--shadow-pop)' }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Message input */}
      {rootMessage && (
        <MessageInput
          onSend={sendReply}
          placeholder="Reply in thread..."
        />
      )}
    </div>
  );
}
